/* eslint-disable no-param-reassign */

// eslint-disable-next-line max-classes-per-file
import Quill from 'quill';
import MagicUrl from 'quill-magic-url';
import ImageResize from '@taoqf/quill-image-resize-module';

import SoftBreak from './soft-break';
import MagicEmail from './magic-email';
import SourceCode from './source-code';
import JotformImage from './jotform-image';
import FileValidator from './file-validator';
import DragAndDropMessages from './dd-messages';
import ImageDropAndPaste from './image-drop-and-paste';

import 'quill/dist/quill.snow.css';
import '../styles/jotform-quill.css';
import 'highlight.js/styles/zenburn.css';

export default class JotformQuill {
  constructor(editorContainerId = '#editor-container', options, editorId = '#editor') {
    const defaults = {
      height: 500,
      maxHeight: 1000,
      onLoad: () => {},
      onTextChanged: () => {},
      editorId,
      editorContainerId
    };
    this.windowScrollY = 0;
    this.isPasted = false;
    this.lastSelectionFocusNode = null;
    this.settings = Object.assign(defaults, options);
    this.initKeyboardShortcuts();
    this.fileValidator = new FileValidator({ maxFileSize: this.settings.maxFileSize });
    this.initLinkHandler();
  }

  actionButtonEvent() {
    const focusedTextIsInView = (editor, selectionPosY) => {
      const editorRect = editor.getBoundingClientRect();
      return selectionPosY <= editorRect.height;
    };

    const scrollToSelectedText = (editor, selectionPos) => {
      editor.scroll({
        top: selectionPos
      });
    };

    const editor = document.querySelector(`${this.settings.editorContainerId} .ql-editor.forumContent`);
    const editorFrame = document.querySelector(`${this.settings.editorContainerId}`);
    if (this.lastSelectionFocusNode) {
      const selectionFocusNodeRect = this.lastSelectionFocusNode.parentElement.getBoundingClientRect();

      if (!this.isInViewport(editor)) {
        editorFrame.scrollIntoView({
          block: 'center'
        });
      }

      if (!focusedTextIsInView(editor, selectionFocusNodeRect.y)) {
        scrollToSelectedText(editor, selectionFocusNodeRect.y);
      }
    }
  }

  snowThemeLinkHandler(value) {
    if (value) {
      const selection = window.getSelection();
      if (selection && selection.focusNode.nodeName === '#text') {
        this.lastSelectionFocusNode = selection.focusNode;
      }
      const range = this.quill.getSelection();
      if (range == null || range.length === 0) return;
      let preview = this.quill.getText(range);
      if (/^\S+@\S+\.\S+$/.test(preview) && preview.indexOf('mailto:') !== 0) {
        preview = `mailto:${preview}`;
      }
      const { tooltip } = this.quill.theme;
      tooltip.edit('link', preview);
    } else {
      this.quill.format('link', false);
    }
    const actionContainer = document.querySelector(`${this.settings.editorContainerId} .ql-action`);
    this.actionButtonEvent = this.actionButtonEvent.bind(this);
    actionContainer.removeEventListener('click', this.actionButtonEvent);
    actionContainer.addEventListener('click', this.actionButtonEvent, { once: true });
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0
      && rect.left >= 0
      && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
      && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  initLinkHandler() {
    const toolbarModule = this.settings.modules.toolbar;
    if (!toolbarModule.handlers) {
      toolbarModule.handlers = {};
    }

    const linkHandler = toolbarModule.handlers.link;
    toolbarModule.handlers.link = value => {
      this.snowThemeLinkHandler(value);
      const editor = document.querySelector(`${this.settings.editorContainerId} .ql-editor.forumContent`);
      editor.scroll({
        top: 0
      });
      if (linkHandler) {
        linkHandler();
      }
    };
  }

  initKeyboardShortcuts() {
    this.settings.modules.keyboard = {
      bindings: {
        shiftEnter: {
          key: 13,
          shiftKey: true,
          handler: this.shiftEnterSoftBreak
        }
      }
    };
  }

  shiftEnterSoftBreak(range) {
    const currentLeaf = this.quill.getLeaf(range.index)[0];
    const nextLeaf = this.quill.getLeaf(range.index + 1)[0];

    this.quill.insertEmbed(range.index, 'smartBreak', true, 'user');

    // Insert a second break if:
    // At the end of the editor, OR next leaf has a different parent (<p>)
    if (!nextLeaf || currentLeaf.parent !== nextLeaf.parent) {
      this.quill.insertEmbed(range.index, 'smartBreak', true, 'user');
    }

    this.quill.setSelection(range.index + 1, 'user');
  }

  validateFile(contentType, fileSize) {
    return this.fileValidator.applyAllValidations(0, contentType, fileSize);
  }

  render() {
    Quill.register({
      'modules/imageDropAndPaste': ImageDropAndPaste,
      'modules/imageResize': ImageResize,
      'modules/magicUrl': MagicUrl,
      'modules/magicEmail': MagicEmail,
      'modules/jotformImage': JotformImage,
      'modules/sourceCode': SourceCode,
      'formats/softbreak': SoftBreak
    }, true);

    const Inline = Quill.import('blots/inline');
    const Block = Quill.import('blots/block');
    const BlockEmbed = Quill.import('blots/block/embed');
    class GrammarlyInline extends Inline {
    }

    GrammarlyInline.tagName = 'G';
    GrammarlyInline.blotName = 'grammarly-inline';
    GrammarlyInline.className = 'gr_';

    Quill.register(GrammarlyInline, true);
    class SplitThreadContent extends Block {
      static tagName = 'div';

      static blotName = 'stc';

      static className = 'split-thread-answered-container';

      static create() {
        const node = super.create();
        return node;
      }
    }
    class SplitThreadAnswer extends BlockEmbed {
      static tagName = 'A';

      static blotName = 'sta';

      static className = 'split-thread-answered-link';

      static create(value) {
        const node = super.create(value);
        const { href } = value;
        if (href) node.setAttribute('href', href);
        node.setAttribute('target', '_blank');
        return node;
      }

      static value(node) {
        return {
          href: node.getAttribute('href')
        };
      }
    }

    class SplitThreadOriginalAnswer extends BlockEmbed {
      static tagName = 'A';

      static blotName = 'stoa';

      static className = 'split-thread-original-link';

      static create(value) {
        const node = super.create(value);
        const { href } = value;
        if (href) node.setAttribute('href', href);
        node.setAttribute('target', '_blank');
        return node;
      }

      static value(node) {
        return {
          href: node.getAttribute('href')
        };
      }
    }

    Quill.register({
      'blots/stc': SplitThreadContent,
      'blots/sta': SplitThreadAnswer,
      'blots/stoa': SplitThreadOriginalAnswer
    }, true);

    this.quill = new Quill(this.settings.editorId, this.settings);
    this.quill.root.removeAttribute('data-gramm');

    this.changeClassNames();

    if (typeof this.settings.onInit === 'function') {
      this.settings.onInit(this.quill);
    }

    window.addEventListener('scroll', () => {
      if (this.isPasted) {
        window.scrollTo({
          top: this.windowScrollY
        });
        this.isPasted = false;
      } else {
        this.windowScrollY = window.scrollY;
      }
    });

    this.quill.root.addEventListener('paste', () => {
      this.isPasted = true;
    }, false);

    this.quill.on('text-change', () => {
      // custom binded autoScaler for editor height;
      this.autoScale();

      // call calback on each text change event.
      if (typeof this.settings.onTextChanged === 'function') {
        this.settings.onTextChanged(this.quill);
      }
    });

    const qlEditorEl = document.querySelector(`${this.settings.editorContainerId} .ql-editor`);

    // inject memberkit answer's CSS class for editor
    qlEditorEl.classList.add('forumContent');

    if (typeof this.settings.onFocus === 'function') {
      qlEditorEl.addEventListener('focus', this.settings.onFocus);
    }

    if (typeof this.settings.onBlur === 'function') {
      qlEditorEl.addEventListener('blur', this.settings.onBlur);
    }

    // if image resize guides are enabled prevent dragged files being dropped on it
    // otherwise page will be redirected as default behaviour
    document.addEventListener('dragover', this.toggleImageResizeGuideOnDrop.bind(this));
    document.addEventListener('drop', this.toggleImageResizeGuideOnDrop.bind(this));

    const jotformImage = this.quill.getModule('jotformImage');
    const ddMessages = new DragAndDropMessages(jotformImage, this.settings.editorContainerId);
    ddMessages.init();

    // call I'm ready callback;
    this.settings.onLoad(this.quill);
  }

  toggleImageResizeGuideOnDrop(e) {
    e.preventDefault();
    const lastEl = document.querySelector(`${this.settings.editorContainerId} .ql-drag-notice`);

    if (!lastEl) return;

    if (lastEl.classList.length) {
      return;
    }

    if (e.type === 'dragover') {
      lastEl.style.display = 'none';
    } else if (e.type === 'drop') {
      lastEl.style.display = 'block';
    }
  }

  changeClassNames() {
    const quillConstructor = this.quill.constructor;
    const moduleNames = ['formats/indent', 'formats/align'];
    moduleNames.forEach(moduleName => {
      const currModule = quillConstructor.import(moduleName);
      if (currModule.keyName) {
        currModule.keyName = currModule.keyName.replace('ql-', 'forum-');
        quillConstructor.register(currModule, true);
      }
    });
  }

  autoScale() {
    setTimeout(() => {
      const qlEditorEl = document.querySelector(`${this.settings.editorContainerId} .ql-editor`);
      const qlToolbarEl = document.querySelector(`${this.settings.editorContainerId} .ql-toolbar`);
      const qlEditorContainer = document.querySelector(this.settings.editorContainerId);

      let contentHeight = this.getContentHeight(qlEditorEl, qlToolbarEl);

      const allowedOffsetHeightMin = this.settings.height - qlToolbarEl.offsetHeight;
      const allowedOffsetHeightMax = this.settings.maxHeight - qlToolbarEl.offsetHeight;

      if (contentHeight >= allowedOffsetHeightMax) {
        contentHeight = allowedOffsetHeightMax;
      } else if (contentHeight <= allowedOffsetHeightMin) {
        contentHeight = allowedOffsetHeightMin;
      }
      qlEditorContainer.style.height = `${contentHeight + qlToolbarEl.offsetHeight}px`;
    }, 100);
  }

  getContentHeight(qlEditorEl) {
    let total = 0;
    const qlEditorStyle = window.getComputedStyle(qlEditorEl);

    [].forEach.call(qlEditorEl.childNodes, currentEl => {
      total += currentEl.offsetHeight + parseInt(window.getComputedStyle(currentEl)
        .getPropertyValue('margin-bottom'), 10);
    });

    total += parseInt(qlEditorStyle.getPropertyValue('padding-top'), 10);
    total += parseInt(qlEditorStyle.getPropertyValue('padding-bottom'), 10);

    return total;
  }
}
